import type { SearchResult } from '@elastic/search-ui'
import { TableRow } from '../style'
import { Col, Row, Table } from 'react-bootstrap'
import {
	getStatusText,
	getTagColor,
	processText,
} from 'components/search/buckets/utils'
import { Tag } from 'components/common'
import parse from 'html-react-parser'
import { useTranslation } from 'react-i18next'
import { CodeSystem } from 'context/elasticsearch/types'
import { useEsConfig } from 'context/elasticsearch/esConfigContext'
import { useState, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'

const capitalizeFirstLetter = (value: string) => {
	if (value.startsWith('<em>')) {
		return value.slice(0, 4) + value.charAt(4).toUpperCase() + value.slice(5)
	}
	return value.charAt(0).toUpperCase() + value.slice(1)
}

function snippetFix(snippet: string) {
	if (!snippet) return ''

	const regex = /\([^\)]+$/
	if (snippet.match(regex)) {
		return snippet + ')'
	}

	return capitalizeFirstLetter(snippet)
}

const highlightSearchTerm = (text: string, searchTerm: string) => {
	// If searchTerm is empty or undefined, return the original text
	if (!searchTerm) {
		return text
	}

	// Escape special characters in the search term
	const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

	// Create a regular expression from the escaped search term
	const regex = new RegExp(`(${escapedSearchTerm})(?![^<]*>|[^<>]*</)`, 'gi')

	// Replace all instances of the search term with the search term wrapped in <em> tags
	const highlightedText = text.replace(regex, '<em>$1</em>')

	return highlightedText
}

const getRaw = (
	result: { [x: string]: { raw: string } },
	value: string | number
) => {
	if (!result[value] || !result[value].raw) return
	return result[value].raw
}

const getSnippet = (
	result: { [x: string]: { snippet: string | undefined; raw: string } },
	value: string | number
) => {
	if (!result[value]) return
	if (!result[value].snippet) {
		return snippetFix(result[value].raw)
	} else {
		return snippetFix('' + result[value].snippet)
	}
}

interface CodeHit {
	code: string
	name: string
	description: string
	score: number
}

const KisSearchResultRow = ({
	result,
	searchTerm,
	displayScore = false,
}: SearchResult) => {
	const { t } = useTranslation()
	const { config } = useEsConfig(CodeSystem.Adm)
	const [showAllHits, setShowAllHits] = useState(false)
	const [showCodeHits, setShowCodeHits] = useState(false)

	// Memoize the computation of code hits to avoid recalculating on every render
	const { codeHits, shouldShowCodes } = useMemo(() => {
		const hits: CodeHit[] = []
		let shouldShow = false

		if (
			result._meta?.rawHit?.inner_hits?.codes?.hits?.hits &&
			Array.isArray(result._meta.rawHit.inner_hits.codes.hits.hits)
		) {
			const rawHits = result._meta.rawHit.inner_hits.codes.hits.hits
			for (const highHit of rawHits.filter(
				(hit: {
					_score: number
					_source?: {
						valid_to?: string
						code?: string
						name_norwegian?: string
						description?: string
					}
				}) =>
					hit._score > config.minimumNestedFieldScore && !hit._source?.valid_to
			)) {
				if (highHit._source?.code) {
					const code = highlightSearchTerm(highHit._source.code, searchTerm)
					const name = highlightSearchTerm(
						highHit._source.name_norwegian || '',
						searchTerm
					)
					const description = highlightSearchTerm(
						highHit._source.description || '',
						searchTerm
					)

					hits.push({
						code: code,
						name: name,
						description: description,
						score: Math.round((highHit._score || 0) * 10) / 10,
					})
					shouldShow = true
				}
			}
		}

		return { codeHits: hits, shouldShowCodes: shouldShow }
	}, [
		result._meta?.rawHit?.inner_hits?.codes?.hits?.hits,
		searchTerm,
		config.minimumNestedFieldScore,
	])

	// Memoize whether to show description column
	const showDescription = useMemo(() => {
		return codeHits.some((hit) => hit.description)
	}, [codeHits])

	const resultScore = useMemo(() => {
		return Math.round((parseFloat(result._meta.rawHit._score) || 0) * 10) / 10
	}, [result._meta.rawHit._score])

	const totalHits = codeHits.length

	return (
		<>
			<TableRow role='row' className='gap-1 gap-md-3 w-100 flex-nowrap'>
				<Col role='cell' className='p-0' xs={1}>
					{getRaw(result, 'code_system_oid') &&
						getRaw(result, 'code_system_oid')}
					{displayScore && (
						<span style={{ fontSize: '0.75em' }}>{` (${resultScore})`}</span>
					)}
				</Col>
				<Col role='cell' xs={3} className='p-0 text-break'>
					{getRaw(result, 'name_norwegian') && (
						<>{processText('name_norwegian', searchTerm, false, result)}</>
					)}
				</Col>
				<Col role='cell' xs={2} className='p-0'>
					{getRaw(result, 'owner_name') && (
						<>{processText('owner_name', searchTerm, false, result)}</>
					)}
				</Col>
				<Col role='cell' xs={3} md={2} className='p-0'>
					{getRaw(result, 'collection_name') &&
						parse(
							highlightSearchTerm(
								getSnippet(result, 'collection_name') as string,
								searchTerm
							)
						)}
				</Col>
				<Col role='cell' xs={2} className='p-0'>
					{getRaw(result, 'category_name') &&
						parse(
							highlightSearchTerm(
								getSnippet(result, 'category_name') as string,
								searchTerm
							)
						)}
				</Col>
				<Col role='cell' xs={1} className='d-flex p-lg-0'>
					{getRaw(result, 'status_text') && (
						<Tag
							color={getTagColor(getRaw(result, 'status_text') as string)}
							text={getStatusText(getRaw(result, 'status_text') as string)}
						/>
					)}
				</Col>
			</TableRow>
			{shouldShowCodes && codeHits.length > 0 && (
				<Row className='d-flex flex-column gap-0 p-0 m-0'>
					<div
						role='button'
						tabIndex={0}
						aria-expanded={showCodeHits}
						aria-label={
							showCodeHits ? 'Skjul treff i koder' : 'Vis treff i koder'
						}
						style={{ cursor: 'pointer', fontSize: '0.8em' }}
						onClick={(e) => {
							e.preventDefault()
							e.stopPropagation()
							setShowCodeHits(!showCodeHits)
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault()
								e.stopPropagation()
								setShowCodeHits(!showCodeHits)
							}
						}}
						className='px-3 py-2'
					>
						<div style={{ display: 'flex', alignItems: 'center' }}>
							<FontAwesomeIcon
								icon={showCodeHits ? faChevronUp : faChevronDown}
								style={{ marginRight: '8px', color: '#6c757d' }}
							/>
							<span style={{ color: '#6c757d' }}>
								{showCodeHits
									? 'Skjul treff i koder'
									: `Vis treff i koder (${totalHits})`}
							</span>
						</div>
					</div>
					{showCodeHits && (
						<Table
							responsive
							className='p-0'
							hover
							size='sm'
							style={
								{
									fontSize: '0.8em',
									tableLayout: 'auto',
									backgroundColor: 'transparent !important',
									'--bs-table-bg': 'transparent',
								} as React.CSSProperties
							}
						>
							<thead>
								<tr>
									{displayScore && <th style={{ width: 'auto' }}>Score</th>}
									<th style={{ width: 'auto' }}>
										{t('adminPage.results.value')}
									</th>
									<th style={{ width: 'auto' }}>
										{t('adminPage.results.name')}
									</th>
									{showDescription && (
										<th style={{ width: 'auto' }}>
											{t('adminPage.results.explaination')}
										</th>
									)}
								</tr>
							</thead>
							<tbody>
								{codeHits.map((hit, i) => {
									return (
										(i < config.maximumNestedFieldValues || showAllHits) && (
											<tr key={`${hit}-${i}`}>
												{displayScore && (
													<td style={{ whiteSpace: 'nowrap' }}>{hit.score}</td>
												)}
												<td style={{ whiteSpace: 'nowrap' }}>
													{parse(hit.code)}
												</td>
												<td>{parse(hit.name)}</td>
												{showDescription && <td>{parse(hit.description)}</td>}
											</tr>
										)
									)
								})}
								{showAllHits ? (
									<tr
										style={{ cursor: 'pointer' }}
										onClick={(e) => {
											e.preventDefault()
											e.stopPropagation()
											setShowAllHits(false)
										}}
									>
										<td
											colSpan={
												showDescription
													? displayScore
														? 4
														: 3
													: displayScore
													? 3
													: 2
											}
											className='px-3'
											style={{
												color: '#6c757d',
											}}
										>
											<div
												style={{
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',
												}}
											>
												<div style={{ display: 'flex', alignItems: 'center' }}>
													<FontAwesomeIcon
														icon={faChevronUp}
														style={{ marginRight: '8px' }}
													/>
													<span>Vis færre treff</span>
												</div>
												<div style={{ display: 'flex', alignItems: 'center' }}>
													<span>
														Viser {totalHits} av {totalHits} treff
													</span>
													<FontAwesomeIcon
														icon={faChevronUp}
														style={{ marginLeft: '8px' }}
													/>
												</div>
											</div>
										</td>
									</tr>
								) : totalHits > config.maximumNestedFieldValues ? (
									<tr
										style={{ cursor: 'pointer' }}
										onClick={(e) => {
											e.preventDefault()
											e.stopPropagation()
											setShowAllHits(true)
										}}
									>
										<td
											colSpan={
												showDescription
													? displayScore
														? 4
														: 3
													: displayScore
													? 3
													: 2
											}
											className='px-3'
											style={{
												color: '#6c757d',
											}}
										>
											<div
												style={{
													display: 'flex',
													justifyContent: 'space-between',
													alignItems: 'center',
												}}
											>
												<div style={{ display: 'flex', alignItems: 'center' }}>
													<FontAwesomeIcon
														icon={faChevronDown}
														style={{ marginRight: '8px' }}
													/>
													<span>
														Vis flere treff (
														{totalHits - config.maximumNestedFieldValues})
													</span>
												</div>
												<div style={{ display: 'flex', alignItems: 'center' }}>
													<span>
														Viser {config.maximumNestedFieldValues} av{' '}
														{totalHits} treff
													</span>
													<FontAwesomeIcon
														icon={faChevronDown}
														style={{ marginLeft: '8px' }}
													/>
												</div>
											</div>
										</td>
									</tr>
								) : (
									<tr>
										<td
											colSpan={
												showDescription
													? displayScore
														? 4
														: 3
													: displayScore
													? 3
													: 2
											}
											className='px-3 text-start'
											style={{
												color: '#6c757d',
												fontSize: '0.85em',
											}}
										>
											<span>
												Viser {totalHits} av {totalHits} treff
											</span>
										</td>
									</tr>
								)}
							</tbody>
						</Table>
					)}
				</Row>
			)}
		</>
	)
}

export default KisSearchResultRow
